<template lang="pug">
.collection-updates
  .collection-updates__main
    .panel
      div
        h2.section-title.mb-1 Note from Organisation
        p.panel__hint Shown as a featured message at the top of the Updates tab on the front end.
      textarea.field-input.field-input--textarea(
        v-model="creatorNote"
        rows="3"
        maxlength="1000"
        placeholder="Write a note from your organisation…"
        @blur="flush"
      )

    .panel
      h2.section-title Post a new update
      .post-form
        el-date-picker.post-form__date(
          v-model="newPostDate"
          type="date"
          format="MMM D"
          :placeholder="todayLabel"
          :clearable="false"
        )
        input.field-input.post-form__body(
          v-model="newPostBody"
          maxlength="2000"
          placeholder="e.g. River Keeper sticker now available at the cleanup event!"
          @keydown.enter="addPost"
        )
        button.btn-primary(type="button" :disabled="savingPost" @click="addPost")
          fai(type="fal" icon="plus").size-16
          | Publish

    .panel
      h2.section-title.mb-4 Update timeline
      p.timeline-empty(v-if="!sortedUpdates.length") No updates posted yet.
      div(v-else)
        .timeline-item(v-for="(post, i) in sortedUpdates" :key="post.id")
          .timeline-item__rail
            .timeline-item__dot
            .timeline-item__line(v-if="i < sortedUpdates.length - 1")
          .timeline-item__content
            p.timeline-item__date
              | {{ formatDate(post.postedAt) }}
              span.timeline-item__scheduled(v-if="isScheduled(post.postedAt)") Scheduled
            template(v-if="editingId === post.id")
              textarea.field-input.field-input--textarea(
                v-model="editBody"
                rows="2"
                maxlength="2000"
              )
              .timeline-item__edit-actions
                button.btn-primary(type="button" @click="saveEdit(post)") Save
                button.btn-outline(type="button" @click="cancelEdit") Cancel
            p.timeline-item__body(v-else) {{ post.body }}
          .timeline-item__actions(v-if="editingId !== post.id")
            button.timeline-item__action(type="button" title="Edit" @click="startEdit(post)")
              fai(type="fal" icon="edit").size-14
            button.timeline-item__action.timeline-item__action--danger(
              type="button"
              title="Delete"
              @click="deletePost(post.id)"
            )
              fai(type="fal" icon="trash").size-14

  aside.collection-updates__preview
    .preview-sticky
      p.preview-label Front-end preview
      .preview-card
        .preview-card__head
          .d-flex.justify-content-between.align-items-start
            div
              h3.preview-card__title {{ collection?.title || 'Collection Title' }}
              p.preview-card__org {{ orgName }}
            span.preview-card__close ×
          p.preview-card__count
            | {{ stickerCount }} sticker{{ stickerCount === 1 ? '' : 's' }}
            |  to complete the collection
        .preview-card__tabs
          button.preview-card__tab(
            v-for="tab in previewTabs"
            :key="tab.id"
            type="button"
            :class="{ 'is-active': previewTab === tab.id }"
            @click="previewTab = tab.id"
          ) {{ tab.label }}
        .preview-card__content(v-if="previewTab === 'stickers'")
          p.preview-empty(v-if="!previewItems.length") No stickers in this collection yet.
          .preview-stickers(v-else)
            sticker-collection-grid-item(
              v-for="item in previewItems"
              :key="item.id"
              :item="item"
            )
        .preview-card__content(v-else)
          .preview-note(v-if="creatorNote")
            p.preview-note__title A note from {{ orgName }}
            p.preview-note__body "{{ creatorNote }}"
          .preview-post(v-for="(post, i) in visibleUpdates" :key="'p-' + post.id")
            .timeline-item__rail
              .preview-post__dot
              .timeline-item__line(v-if="i < visibleUpdates.length - 1")
            .preview-post__content
              p.timeline-item__date {{ formatDate(post.postedAt) }}
              p.preview-post__body {{ post.body }}
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import dayjs from 'dayjs';
import { ElMessage, ElMessageBox } from 'element-plus';
import globalShared from '@/shared';
import {
  mutateStickerCollection,
  mutateStickerCollectionUpdate,
  removeStickerCollectionUpdate,
} from '@/graphs/gql';
import StickerCollectionGridItem from '@/views/web-app/sticker/components/StickerCollectionGridItem.vue';
import type {
  StickerCollectionCms,
  StickerCollectionItem,
} from '@/data/stickerCollection.types';
import withGraphErrors from '@/utils/graphqlResponse';

const PREVIEW_TABS = [
  { id: 'stickers', label: 'STICKERS' },
  { id: 'updates', label: 'UPDATES' },
];

export default defineComponent({
  name: 'CollectionUpdatesTab',
  components: { StickerCollectionGridItem },
  props: {
    collectionId: { type: String, required: true },
    collection: { type: Object as () => StickerCollectionCms | null, default: null },
    orgName: { type: String, default: '' },
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const { makeGraphRequest, getAuthToken } = globalShared();
    const creatorNote = ref('');
    const newPostBody = ref('');
    const newPostDate = ref<Date | null>(null);
    const savingPost = ref(false);
    const editingId = ref<string | null>(null);
    const editBody = ref('');
    const previewTab = ref('stickers');

    watch(
      () => props.collection?.creatorNote,
      (note) => {
        creatorNote.value = note || '';
      },
      { immediate: true },
    );

    const todayLabel = dayjs().format('MMM D');
    // Stickers needed to complete: all of them unless the creator set a lower number.
    const stickerCount = computed(() => {
      const total = props.collection?.items?.length ?? 0;
      const required = props.collection?.requireAllStickersToComplete === false
        ? props.collection?.stickersRequiredToComplete ?? 0
        : 0;
      return required > 0 ? Math.min(required, total) : total;
    });

    // As a new collector sees them: nothing collected yet, clue shown on hover.
    const previewItems = computed<StickerCollectionItem[]>(() => (
      [...(props.collection?.items ?? [])]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((it) => ({
          id: it.id,
          sortOrder: it.sortOrder,
          clue: it.clue,
          requiresCode: it.requiresCode,
          collected: false,
          sticker: it.sticker,
        }))
    ));

    const sortedUpdates = computed(() => {
      const updates = props.collection?.updates ?? [];
      return [...updates].sort(
        (a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime(),
      );
    });

    // A future postedAt schedules the post: collectors see it from that time on.
    function isScheduled(postedAt: string) {
      return dayjs(postedAt).isAfter(dayjs());
    }

    // The front-end preview shows what collectors see now, without scheduled posts.
    const visibleUpdates = computed(
      () => sortedUpdates.value.filter((post) => !isScheduled(post.postedAt)),
    );

    function formatDate(iso: string) {
      return dayjs(iso).format('MMM D');
    }

    /** Saves a changed note. Called on blur and by "Save Collection". */
    async function flush(): Promise<boolean> {
      if (creatorNote.value === (props.collection?.creatorNote || '')) return true;
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollection,
        { input: { id: props.collectionId, creatorNote: creatorNote.value } },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to save note');
        return false;
      }
      emit('refresh');
      return true;
    }

    async function addPost() {
      if (!newPostBody.value.trim() || savingPost.value) return;
      savingPost.value = true;
      try {
        // A past or future day keeps the current time so same-day posts stay ordered.
        const now = dayjs();
        const postedAt = newPostDate.value
          ? dayjs(newPostDate.value)
            .hour(now.hour()).minute(now.minute()).second(now.second())
            .toISOString()
          : now.toISOString();
        const response = await withGraphErrors(makeGraphRequest(
          'mutation',
          mutateStickerCollectionUpdate,
          {
            input: {
              collectionId: props.collectionId,
              body: newPostBody.value.trim(),
              postedAt,
            },
          },
          getAuthToken(),
        ));
        if (response?.errors?.length) {
          ElMessage.error(response.errors[0]?.message || 'Failed to publish update');
          return;
        }
        newPostBody.value = '';
        newPostDate.value = null;
        emit('refresh');
      } finally {
        savingPost.value = false;
      }
    }

    function startEdit(post: { id: string; body: string }) {
      editingId.value = post.id;
      editBody.value = post.body;
    }

    function cancelEdit() {
      editingId.value = null;
      editBody.value = '';
    }

    async function saveEdit(post: { id: string; postedAt: string }) {
      if (!editBody.value.trim()) return;
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        mutateStickerCollectionUpdate,
        {
          input: {
            id: post.id,
            collectionId: props.collectionId,
            body: editBody.value.trim(),
            postedAt: post.postedAt,
          },
        },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to update post');
        return;
      }
      cancelEdit();
      emit('refresh');
    }

    async function deletePost(updateId: string) {
      try {
        await ElMessageBox.confirm('Delete this update?', 'Delete update', {
          type: 'warning',
          customClass: 'c-confirm c-confirm--danger',
          confirmButtonText: 'Delete',
        });
      } catch {
        return;
      }
      const response = await withGraphErrors(makeGraphRequest(
        'mutation',
        removeStickerCollectionUpdate,
        { updateId },
        getAuthToken(),
      ));
      if (response?.errors?.length) {
        ElMessage.error(response.errors[0]?.message || 'Failed to delete post');
        return;
      }
      emit('refresh');
    }

    return {
      creatorNote,
      newPostBody,
      newPostDate,
      savingPost,
      sortedUpdates,
      editingId,
      editBody,
      todayLabel,
      isScheduled,
      visibleUpdates,
      stickerCount,
      previewTabs: PREVIEW_TABS,
      previewTab,
      previewItems,
      formatDate,
      flush,
      addPost,
      startEdit,
      cancelEdit,
      saveEdit,
      deletePost,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '../collection-cms';

// flex gap-6 max-w-5xl
.collection-updates {
  display: flex;
  gap: 24px;
  max-width: 1024px;
  align-items: flex-start;
}

.collection-updates__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.panel {
  @include card;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__hint {
    @include text-xs;
    color: $gray-400;
    margin: 0;
  }
}

.section-title {
  @include section-title;
}

.field-input {
  @include input;

  &--textarea {
    resize: none;
  }
}

.btn-primary {
  @include btn-primary;
}

.btn-outline {
  @include btn-outline;
  padding: 8px 12px;
}

.post-form {
  display: flex;
  gap: 12px;

  &__body {
    flex: 1;
    width: auto;
  }

  .btn-primary {
    padding: 8px 16px;
  }

  // w-28 input styled like the design's text field. The picker root has no scoped attribute.
  :deep(.post-form__date) {
    --el-date-editor-width: 112px;
    --el-input-height: 42px;
    width: 112px;
    flex-shrink: 0;

    .el-input__wrapper {
      height: 100%;
      background: $gray-50;
      border-radius: 8px;
      box-shadow: 0 0 0 1px $gray-200 inset;
      padding: 0 12px;
    }

    .el-input__wrapper.is-focus {
      box-shadow: 0 0 0 1px $teal-light inset, 0 0 0 1px $teal-light;
    }

    .el-input__prefix,
    .el-input__suffix {
      display: none;
    }

    .el-input__inner {
      padding: 0;
      font-size: 14px;
      color: $gray-800;

      &::placeholder {
        color: $gray-400;
      }
    }
  }
}

.timeline-empty {
  @include text-sm;
  color: $gray-400;
  text-align: center;
  padding: 24px 0;
  margin: 0;
}

.timeline-item {
  display: flex;
  gap: 16px;

  &__rail {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__dot {
    width: 12px;
    height: 12px;
    margin-top: 2px;
    border-radius: 9999px;
    background: $teal;
    flex-shrink: 0;
  }

  &__line {
    width: 1px;
    flex: 1;
    background: $gray-200;
    margin: 4px 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
    padding-bottom: 20px;
  }

  // text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1
  &__scheduled {
    margin-left: 8px;
    padding: 1px 6px;
    border-radius: 999px;
    background: $amber-50;
    color: $amber-600;
    text-transform: none;
    letter-spacing: 0;
  }

  &__date {
    @include text-xs;
    font-weight: 600;
    color: $gray-400;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 4px;
  }

  &__body {
    @include text-sm;
    line-height: 1.625;
    color: $gray-700;
    margin: 0;
    white-space: pre-line;
  }

  &__edit-actions {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }

  &__actions {
    display: flex;
    gap: 4px;
    margin-top: 2px;
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.15s;
  }

  &:hover &__actions {
    opacity: 1;
  }

  &__action {
    @include icon-btn;
    padding: 0 2px;
    color: $gray-300;

    &:hover:not(:disabled) {
      color: $gray-600;
    }

    &--danger:hover:not(:disabled) {
      color: $red-400;
    }
  }
}

// w-72 flex-shrink-0, sticky top-0
.collection-updates__preview {
  width: 288px;
  flex-shrink: 0;
  align-self: stretch;
}

.preview-sticky {
  position: sticky;
  top: 0;
}

.preview-label {
  @include text-xs;
  font-weight: 600;
  color: $gray-400;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 12px;
}

// bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden
.preview-card {
  background: white;
  border: 1px solid $gray-200;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
  overflow: hidden;

  &__head {
    padding: 20px 20px 16px;
    border-bottom: 1px solid $gray-100;
  }

  &__title {
    font-size: 16px;
    line-height: 1.25;
    font-weight: 700;
    color: $gray-900;
    margin: 0;
  }

  &__org {
    @include text-sm;
    font-weight: 500;
    color: $teal;
    margin: 2px 0 0;
  }

  &__close {
    color: $gray-300;
    font-size: 18px;
    line-height: 1;
  }

  &__count {
    @include text-sm;
    color: $gray-400;
    margin: 8px 0 0;
  }

  &__tabs {
    display: flex;
    border-bottom: 1px solid $gray-100;
  }

  &__tab {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    font-family: inherit;
    cursor: pointer;
    @include text-xs;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: $gray-400;

    &.is-active {
      color: $gray-900;
      border-bottom: 2px solid $gray-900;
    }
  }

  &__content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 320px;
    overflow-y: auto;
  }
}

.preview-stickers {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.preview-empty {
  @include text-xs;
  color: $gray-400;
  text-align: center;
  padding: 24px 0;
  margin: 0;
}

.preview-note {
  background: $gray-50;
  border: 1px solid $gray-100;
  border-radius: 12px;
  padding: 12px;

  &__title {
    @include text-xs;
    font-weight: 700;
    color: $gray-700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 6px;
  }

  &__body {
    @include text-xs;
    line-height: 1.625;
    color: $gray-600;
    font-style: italic;
    margin: 0;
  }
}

.preview-post {
  display: flex;
  gap: 12px;

  &__dot {
    width: 10px;
    height: 10px;
    margin-top: 2px;
    border-radius: 9999px;
    background: $teal;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    padding-bottom: 12px;
  }

  &__body {
    @include text-xs;
    line-height: 1.625;
    color: $gray-700;
    margin: 0;
  }
}
</style>
